/**
 * HelpCenterPage
 * Central de Ajuda — hub de tutoriais navegável.
 * Rotas: /ajuda (índice) e /ajuda/:entity/:screen (tutorial em markdown).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import BrandLogo from '../../components/common/BrandLogo';
import { buscarTutorial, comVersaoDoBuild, useHelpManifest, type HelpEntity } from './useHelpManifest';

const SIDEBAR_WIDTH = 300;

interface HelpRouteParams extends Record<string, string | undefined> {
  entity?: string;
  screen?: string;
}

interface MarkdownState {
  content: string | null;
  loading: boolean;
  error: string | null;
}

function filterEntitiesBySearch(entities: HelpEntity[], term: string): HelpEntity[] {
  const normalized = term.trim().toLowerCase();
  if (!normalized) return entities;

  return entities
    .map((entity) => ({
      ...entity,
      screens: entity.screens.filter(
        (screen) =>
          screen.title.toLowerCase().includes(normalized) ||
          entity.label.toLowerCase().includes(normalized)
      ),
    }))
    .filter((entity) => entity.screens.length > 0);
}

const HelpCenterPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const params = useParams<HelpRouteParams>();
  const { entity: entityParam, screen: screenParam } = params;

  const { manifest, loading: manifestLoading, error: manifestError } = useHelpManifest();

  const [searchTerm, setSearchTerm] = useState('');
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const filteredEntities = useMemo(
    () => filterEntitiesBySearch(manifest?.entities ?? [], searchTerm),
    [manifest, searchTerm]
  );

  const selectedEntity = useMemo(
    () => manifest?.entities.find((entity) => entity.id === entityParam) ?? null,
    [manifest, entityParam]
  );

  const selectedScreen = useMemo(
    () => selectedEntity?.screens.find((screen) => screen.id === screenParam) ?? null,
    [selectedEntity, screenParam]
  );

  const hasRouteSelection = Boolean(entityParam && screenParam);
  const screenNotFound = hasRouteSelection && !manifestLoading && !manifestError && !selectedScreen;

  const [markdownState, setMarkdownState] = useState<MarkdownState>({
    content: null,
    loading: false,
    error: null,
  });

  useEffect(() => {
    if (!selectedScreen) {
      setMarkdownState({ content: null, loading: false, error: null });
      return;
    }

    let cancelled = false;

    async function loadMarkdown(file: string) {
      setMarkdownState({ content: null, loading: true, error: null });
      try {
        const response = await buscarTutorial(`/tutoriais/${file}`);
        if (!response.ok) {
          throw new Error(`Falha ao carregar o tutorial (HTTP ${response.status})`);
        }
        const text = await response.text();
        if (!cancelled) {
          setMarkdownState({ content: text, loading: false, error: null });
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setMarkdownState({
            content: null,
            loading: false,
            error: err instanceof Error ? err.message : 'Erro ao carregar o tutorial',
          });
        }
      }
    }

    void loadMarkdown(selectedScreen.file);

    return () => {
      cancelled = true;
    };
  }, [selectedScreen]);

  const handleSelectScreen = (entityId: string, screenId: string) => {
    navigate(`/ajuda/${entityId}/${screenId}`);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const markdownComponents: Components = useMemo(
    () => ({
      h1: ({ children }) => (
        <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mt: 3, mb: 1.5 }}>
          {children}
        </Typography>
      ),
      h2: ({ children }) => (
        <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mt: 3, mb: 1.5 }}>
          {children}
        </Typography>
      ),
      h3: ({ children }) => (
        <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700, mt: 2.5, mb: 1 }}>
          {children}
        </Typography>
      ),
      h4: ({ children }) => (
        <Typography variant="subtitle2" component="h4" sx={{ fontWeight: 700, mt: 2, mb: 1 }}>
          {children}
        </Typography>
      ),
      p: ({ children }) => (
        <Typography variant="body1" component="p" sx={{ mb: 1.5, lineHeight: 1.7 }}>
          {children}
        </Typography>
      ),
      a: ({ href, children }) => (
        <Typography
          component="a"
          href={href}
          target={href?.startsWith('http') ? '_blank' : undefined}
          rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
          sx={{ color: 'primary.main', fontWeight: 600, textDecoration: 'underline' }}
        >
          {children}
        </Typography>
      ),
      ul: ({ children }) => (
        <Box component="ul" sx={{ pl: 3, mb: 1.5 }}>
          {children}
        </Box>
      ),
      ol: ({ children }) => (
        <Box component="ol" sx={{ pl: 3, mb: 1.5 }}>
          {children}
        </Box>
      ),
      li: ({ children }) => (
        <Typography component="li" variant="body1" sx={{ mb: 0.5, lineHeight: 1.7 }}>
          {children}
        </Typography>
      ),
      blockquote: ({ children }) => (
        <Box
          sx={{
            borderLeft: `4px solid ${theme.palette.divider}`,
            pl: 2,
            py: 0.5,
            my: 2,
            color: 'text.secondary',
            fontStyle: 'italic',
          }}
        >
          {children}
        </Box>
      ),
      hr: () => <Divider sx={{ my: 3 }} />,
      img: ({ src, alt }) => (
        <Box
          component="img"
          src={typeof src === 'string' ? comVersaoDoBuild(src) : undefined}
          alt={alt ?? ''}
          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
            // Print ainda não capturado: oculta a imagem quebrada em vez de mostrar o ícone de erro.
            e.currentTarget.style.display = 'none';
          }}
          sx={{ maxWidth: '100%', borderRadius: 1, boxShadow: 1, my: 2, display: 'block' }}
        />
      ),
      pre: ({ children }) => (
        <Box
          component="pre"
          sx={{
            backgroundColor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100',
            color: 'text.primary',
            p: 2,
            borderRadius: 1,
            overflowX: 'auto',
            my: 2,
            fontSize: '0.85rem',
          }}
        >
          {children}
        </Box>
      ),
      code: ({ children }) => (
        <Box
          component="code"
          sx={{
            backgroundColor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
            px: 0.6,
            py: 0.2,
            borderRadius: 0.5,
            fontFamily: 'monospace',
            fontSize: '0.85em',
          }}
        >
          {children}
        </Box>
      ),
      table: ({ children }) => (
        <TableContainer component={Paper} variant="outlined" sx={{ my: 2, overflowX: 'auto' }}>
          <Table size="small">{children}</Table>
        </TableContainer>
      ),
      thead: ({ children }) => <TableHead>{children}</TableHead>,
      tbody: ({ children }) => <TableBody>{children}</TableBody>,
      tr: ({ children }) => <TableRow>{children}</TableRow>,
      th: ({ children }) => (
        <TableCell sx={{ fontWeight: 700, backgroundColor: 'action.hover' }}>{children}</TableCell>
      ),
      td: ({ children }) => <TableCell>{children}</TableCell>,
    }),
    [theme]
  );

  const sidebarContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Buscar tutorial..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Divider />
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {manifestLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!manifestLoading && manifestError && (
          <Alert severity="error" sx={{ m: 2 }}>
            {manifestError}
          </Alert>
        )}

        {!manifestLoading && !manifestError && (manifest?.entities.length ?? 0) === 0 && (
          <Alert severity="info" sx={{ m: 2 }}>
            Nenhum tutorial disponível ainda.
          </Alert>
        )}

        {!manifestLoading &&
          !manifestError &&
          (manifest?.entities.length ?? 0) > 0 &&
          filteredEntities.length === 0 && (
            <Alert severity="info" sx={{ m: 2 }}>
              Nenhum resultado encontrado para &quot;{searchTerm}&quot;.
            </Alert>
          )}

        {!manifestLoading && !manifestError && filteredEntities.length > 0 && (
          <List
            component="nav"
            dense
            subheader={<li />}
            sx={{
              '& .MuiListSubheader-root': {
                lineHeight: '2.2rem',
                fontWeight: 700,
                color: 'text.secondary',
              },
            }}
          >
            {filteredEntities.map((entity) => (
              <li key={entity.id}>
                <ul style={{ padding: 0 }}>
                  <ListSubheader disableSticky>{entity.label}</ListSubheader>
                  {entity.screens.map((screen) => {
                    const isActive = entity.id === entityParam && screen.id === screenParam;
                    return (
                      <ListItemButton
                        key={screen.id}
                        selected={isActive}
                        onClick={() => handleSelectScreen(entity.id, screen.id)}
                        sx={{ pl: 3 }}
                      >
                        <ListItemText
                          primary={screen.title}
                          slotProps={{
                            primary: {
                              sx: {
                                fontSize: '0.9rem',
                                fontWeight: isActive ? 700 : 400,
                              },
                            },
                          }}
                        />
                      </ListItemButton>
                    );
                  })}
                </ul>
              </li>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'background.default' }}>
      <Toolbar sx={{ borderBottom: `1px solid ${theme.palette.divider}`, gap: 1 }}>
        {isMobile && (
          <IconButton edge="start" onClick={() => setMobileOpen(true)} aria-label="abrir menu de tutoriais">
            <MenuIcon />
          </IconButton>
        )}
        <IconButton onClick={() => navigate('/')} aria-label="voltar ao app">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>
          Central de Ajuda
        </Typography>
      </Toolbar>

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {isMobile ? (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{ '& .MuiDrawer-paper': { width: SIDEBAR_WIDTH } }}
          >
            {sidebarContent}
          </Drawer>
        ) : (
          <Box
            sx={{
              width: SIDEBAR_WIDTH,
              flexShrink: 0,
              borderRight: `1px solid ${theme.palette.divider}`,
              overflow: 'hidden',
            }}
          >
            {sidebarContent}
          </Box>
        )}

        <Box sx={{ flex: 1, overflowY: 'auto', p: { xs: 2, md: 4 } }}>
          <Box sx={{ maxWidth: 860, mx: 'auto' }}>
            {!hasRouteSelection && (
              <Stack spacing={2} alignItems="flex-start">
                <BrandLogo variant="dark" size="sm" />
                <Stack direction="row" spacing={1} alignItems="center">
                  <HelpOutlineIcon color="primary" />
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    Central de Ajuda
                  </Typography>
                </Stack>
                <Typography variant="body1" color="text.secondary">
                  Selecione um tutorial na lista ao lado para ver o passo a passo de cada tela do
                  sistema. Use a busca para encontrar rapidamente o assunto desejado.
                </Typography>
              </Stack>
            )}

            {hasRouteSelection && screenNotFound && (
              <Alert severity="warning">Tutorial não encontrado.</Alert>
            )}

            {hasRouteSelection && selectedScreen && (
              <>
                {markdownState.loading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                  </Box>
                )}

                {!markdownState.loading && markdownState.error && (
                  <Alert severity="error">{markdownState.error}</Alert>
                )}

                {!markdownState.loading && !markdownState.error && markdownState.content && (
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                    {markdownState.content}
                  </ReactMarkdown>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default HelpCenterPage;
